import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';
import HighlightImage from 'images/highlights/case-study-highlight.png';
import Image from 'next/image';
import NextLink from 'next/link';

import { TextDecorationPng } from '@/components/functional';
import { ClutchWidget, TagGroup } from '@/components/molecules';
import { CaseStudy } from '@/graphql/generated';
import { getImageAlt, getImageSrcWithBlurhash } from '@/utils/images';
import { CaseStudiesPath, getDynamicPath } from '@/utils/models';

import { blinkingEyeAnimation, caseStudyStyles } from '../../home/CaseStudies/styles';

type FeaturedCaseStudyComponentProps = {
  caseStudy: CaseStudy;
};
export const FeaturedCaseStudyComponent = ({ caseStudy }: FeaturedCaseStudyComponentProps) => {
  const tags = caseStudy.caseStudyTags?.data.map((el) => el.attributes?.name || '') || [];
  const category = caseStudy?.caseStudyCategory?.data?.attributes?.name || '';

  return (
    <Link
      as={NextLink}
      href={getDynamicPath(CaseStudiesPath.CASE_STUDY, caseStudy.slug)}
      _hover={{ textDecoration: 'none' }}
      zIndex="0"
    >
      <Flex
        className="caseStudy"
        id="featuredCaseStudy"
        sx={caseStudyStyles}
        flexDirection={{
          base: 'column',
          lg: 'row',
        }}
        gap={6}
      >
        <Box
          width="100%"
          minH="434px"
          maxH="434px"
          position="relative"
          overflow="hidden"
          borderRadius="16px"
          flex={{
            base: 3,
            '2xl': 2,
          }}
          _hover={{
            animation: `${blinkingEyeAnimation} 3s infinite`,
          }}
        >
          <Image
            {...getImageSrcWithBlurhash(caseStudy?.image?.data?.attributes)}
            alt={getImageAlt(caseStudy?.image?.data?.attributes, caseStudy.title)}
            fill
            style={{ objectFit: 'cover' }}
            className="caseStudyImage"
          />
        </Box>

        <Flex
          flex={2}
          flexDirection="column"
          justifyContent="center"
          w={{
            base: '100%',
            sm: '70%',
            lg: '100%',
          }}
        >
          <Flex mb={4}>
            <TagGroup tags={[category]} variant="category" mr={2} />
            <TagGroup tags={tags} variant="light" />
          </Flex>
          <Heading
            as="h3"
            variant={{
              base: 'text-4xl/lineHeight-10/font-semibold',
              sm: 'text-6xl/lineHeight-12/font-semibold',
            }}
            color="purple.500"
            position="relative"
            whiteSpace="nowrap"
            mb={6}
          >
            <TextDecorationPng image={HighlightImage} scaleX={1.1}>
              {caseStudy.title}
            </TextDecorationPng>
          </Heading>
          <Text
            variant={{
              base: 'text-md/lineHeight-6/font-normal',
              sm: 'text-md/lineHeight-6/font-semibold',
            }}
            color="purple.400"
            mb={6}
          >
            {caseStudy.description}
          </Text>
          <ClutchWidget />
        </Flex>
      </Flex>
    </Link>
  );
};
